import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Drawer,
  Form,
  Select,
  Switch,
  Button,
  Space,
  Spin,
  Alert,
  Descriptions,
  Tag,
  Typography,
  Popconfirm,
  Empty,
  Input,
} from "antd";
import {
  SaveOutlined,
  DeleteOutlined,
  ReloadOutlined,
  StarFilled,
} from "@ant-design/icons";
import productBucketsApi from "./productBucketsData";
import { triggerNotification } from "../../common/notify";

const { TextArea } = Input;

const renderBucketOption = (b) => ({
  value: Number(b.id),
  label: (
    <span>
      <Typography.Text code style={{ marginRight: 6 }}>
        {b.bucketKey}
      </Typography.Text>
      <Typography.Text>{b.displayName}</Typography.Text>
    </span>
  ),
  bucketKey: String(b.bucketKey || ""),
  displayName: String(b.displayName || ""),
});

const ProductBucketDrawer = ({
  open,
  product,
  storeHash,
  storeLabel,
  onClose,
  onSaved,
}) => {
  const variantIdRaw = product?.raw?.variant_id ?? product?.variantId ?? null;
  const variantId = variantIdRaw == null ? null : Number(variantIdRaw);
  const variantIdValid =
    Number.isInteger(variantId) && variantId > 0;

  const [form] = Form.useForm();

  const [loadingAssignment, setLoadingAssignment] = useState(false);
  const [loadingBuckets, setLoadingBuckets] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const [assignment, setAssignment] = useState(null);
  const [buckets, setBuckets] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [existingDefaults, setExistingDefaults] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [formDirty, setFormDirty] = useState(false);
  const initialValuesRef = useRef(null);

  const checkDirty = useCallback(() => {
    const current = JSON.stringify(form.getFieldsValue(true));
    setFormDirty(current !== initialValuesRef.current);
  }, [form]);

  const resetState = useCallback(() => {
    form.resetFields();
    setAssignment(null);
    setBuckets([]);
    setProductTypes([]);
    setExistingDefaults([]);
    setLoadError(null);
    setSaving(false);
    setRemoving(false);
    setLoadingAssignment(false);
    setLoadingBuckets(false);
    setFormDirty(false);
    initialValuesRef.current = null;
  }, [form]);

  const loadAll = useCallback(async () => {
    if (!variantIdValid || !storeHash) return;
    setLoadError(null);
    setLoadingAssignment(true);
    setLoadingBuckets(true);
    let assignmentResolved = null;
    try {
      const [a, list, defaults, ptypes] = await Promise.all([
        productBucketsApi.fetchAssignment(variantId, storeHash),
        productBucketsApi.fetchBucketsForStore(storeHash),
        productBucketsApi.fetchExistingDefaultsForStore(storeHash),
        productBucketsApi.fetchProductTypesForStore(storeHash),
      ]);
      assignmentResolved = a;
      setAssignment(a);
      setBuckets(list);
      setProductTypes(ptypes);
      setExistingDefaults(defaults);
      form.setFieldsValue({
        bucket_id: a ? Number(a.bucketId) : undefined,
        prod_type_id: a ? a.productType?.id ?? a.productTypeId ?? undefined : undefined,
        is_default: a && Number(a.isDefault) === 1 ? 1 : 0,
        custom_text: a?.customText ?? "",
      });
      initialValuesRef.current = JSON.stringify(form.getFieldsValue(true));
      setFormDirty(false);
    } catch (err) {
      const mapped = productBucketsApi.mapError(err);
      setLoadError(mapped);
      triggerNotification({
        type: "error",
        title: "Failed to load assignment",
        message: mapped.message,
      });
    } finally {
      setLoadingAssignment(false);
      setLoadingBuckets(false);
    }
    return assignmentResolved;
  }, [variantId, variantIdValid, storeHash, form]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    if (variantIdValid && storeHash) {
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, variantId, storeHash]);

  const bucketOptions = useMemo(
    () => buckets.map(renderBucketOption),
    [buckets]
  );

  const productTypeOptions = useMemo(
    () => productTypes.map((p) => ({ value: Number(p.id), label: p.typeName })),
    [productTypes]
  );

  const currentBucket = useMemo(() => {
    if (!assignment) return null;
    return buckets.find((b) => String(b.id) === String(assignment.bucketId));
  }, [assignment, buckets]);

  const defaultsByBucket = useMemo(() => {
    const map = {};
    (existingDefaults || []).forEach((row) => {
      if (row?.bucketId) map[String(row.bucketId)] = row;
    });
    return map;
  }, [existingDefaults]);

  const watchedBucketId = Form.useWatch("bucket_id", form);
  const watchedIsDefault = Form.useWatch("is_default", form);
  const watchedProdTypeId = Form.useWatch("prod_type_id", form);

  const isBucketRequired = useMemo(() => {
    if (!watchedProdTypeId) return false;
    const selected = productTypes.find((p) => Number(p.id) === Number(watchedProdTypeId));
    return selected?.typeName?.toLowerCase() === "essential";
  }, [watchedProdTypeId, productTypes]);

  const isBucketDisabled = useMemo(() => {
    if (!watchedProdTypeId) return false;
    const selected = productTypes.find((p) => Number(p.id) === Number(watchedProdTypeId));
    return selected?.typeName?.toLowerCase() === "enhanced";
  }, [watchedProdTypeId, productTypes]);

  useEffect(() => {
    if (isBucketDisabled) {
      form.setFieldsValue({ bucket_id: undefined });
      checkDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBucketDisabled]);

  const wouldReplaceDefault = useMemo(() => {
    if (Number(watchedIsDefault) !== 1) return null;
    if (!watchedBucketId) return null;
    const existing = defaultsByBucket[String(watchedBucketId)];
    if (!existing) return null;
    if (String(existing.variantId) === String(variantId)) return null;
    return existing;
  }, [watchedBucketId, watchedIsDefault, defaultsByBucket, variantId]);

  const replacedBucket = useMemo(() => {
    if (!wouldReplaceDefault) return null;
    return buckets.find(
      (b) => String(b.id) === String(wouldReplaceDefault.bucketId)
    );
  }, [wouldReplaceDefault, buckets]);

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!variantIdValid || !storeHash || !storeLabel) {
      triggerNotification({
        type: "error",
        title: "Missing context",
        message: "Cannot save — store or variant info is missing.",
      });
      return;
    }
    const bucketIdNum =
      values.bucket_id == null ? null : Number(values.bucket_id);
    const isDefaultNum = Number(values.is_default) === 1 ? 1 : 0;
    const productTypeIdVal =
      values.prod_type_id === undefined || values.prod_type_id === null
        ? null
        : Number(values.prod_type_id);
    const currentProductTypeId = assignment
      ? assignment.productType?.id ?? assignment.productTypeId ?? null
      : null;
    const customTextVal = (values.custom_text || "").trim().slice(0, 255) || null;
    const sameBucket =
      assignment && Number(assignment.bucketId) === bucketIdNum;
    const sameDefault =
      assignment &&
      (Number(assignment.isDefault) === 1 ? 1 : 0) === isDefaultNum;
    const sameProductType =
      assignment &&
      Number(currentProductTypeId ?? 0) === Number(productTypeIdVal ?? 0);
    const sameCustomText =
      assignment && (assignment.customText ?? null) === customTextVal;

    if (sameBucket && sameDefault && sameProductType && sameCustomText) {
      triggerNotification({
        type: "info",
        title: "No changes",
        message: "This product is already assigned to that bucket with the same settings.",
      });
      return;
    }

    setSaving(true);
    try {
      const { action, data } = await productBucketsApi.upsertAssignment({
        variant_id: variantId,
        bucket_id: bucketIdNum,
        prod_type_id: productTypeIdVal,
        store_hash: storeHash,
        store_label: storeLabel,
        is_default: isDefaultNum,
        custom_text: customTextVal,
        bucket_required: isBucketRequired,
      });
      setAssignment(data);
      const savedBucket = buckets.find(
        (b) => String(b.id) === String(data?.bucketId)
      );
      const variantName =
        product?.sapName ||
        product?.raw?.prod_name ||
        product?.bcSku ||
        `Variant ${data?.variantId ?? variantId}`;
      const bucketDisplay = savedBucket
        ? `${savedBucket.displayName} (${savedBucket.bucketKey})`
        : `bucket #${data?.bucketId ?? bucketIdNum}`;
      const defaultSuffix = isDefaultNum === 1 ? " · marked as default" : "";
      triggerNotification({
        type: "success",
        title: action === "created" ? "Bucket assigned" : "Bucket updated",
        message: `${variantName} → ${bucketDisplay} · ${
          data?.storeLabel || storeLabel
        }${defaultSuffix}`,
      });
      onSaved?.({ action, data });
      onClose?.();
    } catch (err) {
      const mapped = productBucketsApi.mapError(err);
      triggerNotification({
        type: "error",
        title: "Save failed",
        message: mapped.message,
      });
      if (mapped.code === "VALIDATION_ERROR" && Array.isArray(mapped.details)) {
        const bucketDetail = mapped.details.find((d) => d?.path === "bucket_id");
        if (bucketDetail) {
          form.setFields([
            { name: "bucket_id", errors: [bucketDetail.message || "Invalid bucket"] },
          ]);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!assignment || !variantIdValid || !storeHash) return;
    setRemoving(true);
    try {
      await productBucketsApi.deleteAssignment(variantId, storeHash);
      const variantName =
        product?.sapName ||
        product?.raw?.prod_name ||
        product?.bcSku ||
        `Variant ${variantId}`;
      const fromBucket = currentBucket
        ? `${currentBucket.displayName} (${currentBucket.bucketKey})`
        : `bucket #${assignment?.bucketId}`;
      triggerNotification({
        type: "success",
        title: "Assignment removed",
        message: `${variantName} is no longer in ${fromBucket} · ${storeLabel}`,
      });
      setAssignment(null);
      form.setFieldsValue({ bucket_id: undefined, is_default: 0, custom_text: "" });
      onSaved?.({ action: "deleted", data: null });
      onClose?.();
    } catch (err) {
      const mapped = productBucketsApi.mapError(err);
      if (mapped.code === "NOT_FOUND") {
        setAssignment(null);
        form.setFieldsValue({ bucket_id: undefined, is_default: 0 });
        triggerNotification({
          type: "warning",
          title: "Already removed",
          message: "This assignment no longer exists.",
        });
      } else {
        triggerNotification({
          type: "error",
          title: "Remove failed",
          message: mapped.message,
        });
      }
    } finally {
      setRemoving(false);
    }
  };

  const productLabel = useMemo(() => {
    if (!product) return "";
    return product.sapName || product.raw?.prod_name || product.bcSku || `#${product.id}`;
  }, [product]);

  const isBusy = saving || removing || loadingAssignment || loadingBuckets;
  const showContextError = !variantIdValid || !storeHash || !storeLabel;
  const noBucketsAvailable = !loadingBuckets && buckets.length === 0 && !loadError && isBucketRequired;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      title="Manage bucket assignment"
      destroyOnClose
      maskClosable={false}
      keyboard={!isBusy}
      closable={!isBusy}
      extra={
        !showContextError && (
          <Button
            icon={<ReloadOutlined />}
            onClick={loadAll}
            disabled={isBusy}
            title="Reload assignment and buckets"
          />
        )
      }
      footer={
        showContextError ? null : (
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            {assignment ? (
              <Popconfirm
                title="Remove bucket assignment?"
                description={
                  <span>
                    Variant <b>{variantId}</b> will be unassigned from{" "}
                    <b>{currentBucket?.displayName || `bucket ${assignment.bucketId}`}</b>
                    {" "}for <b>{storeLabel}</b>.
                  </span>
                }
                okText="Remove"
                okButtonProps={{ danger: true, loading: removing }}
                onConfirm={handleRemove}
                disabled={isBusy}
              >
                <Button danger icon={<DeleteOutlined />} disabled={isBusy} loading={removing}>
                  Remove
                </Button>
              </Popconfirm>
            ) : (
              <span />
            )}
            <Space>
              <Button onClick={onClose} disabled={isBusy}>
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                disabled={isBusy || loadingBuckets || (isBucketRequired && buckets.length === 0) || !formDirty}
              >
                Save
              </Button>
            </Space>
          </Space>
        )
      }
    >
      {showContextError ? (
        <Alert
          type="warning"
          showIcon
          message="Cannot manage this product's bucket"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {!variantIdValid && (
                <li>
                  This product has no usable <b>variant_id</b>. Bucket assignment is keyed by BC
                  variant id; products without one cannot be assigned.
                </li>
              )}
              {!storeHash && (
                <li>
                  Store <b>{storeLabel || product?.store}</b> has no BC store_hash configured.
                  Add it to <code>BlogsTable/stores.js</code> first.
                </li>
              )}
              {!storeLabel && (
                <li>Store label could not be resolved for this product.</li>
              )}
            </ul>
          }
        />
      ) : (
        <Spin spinning={loadingAssignment || loadingBuckets}>
          {loadError && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message={loadError.message}
              action={
                <Button size="small" onClick={loadAll}>
                  Retry
                </Button>
              }
            />
          )}

          {productLabel && (
            <Typography.Text strong style={{ fontSize: 14, display: "block", marginBottom: 12 }}>
              {productLabel}
            </Typography.Text>
          )}

          <Typography.Text
            type="secondary"
            style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}
          >
            Product
          </Typography.Text>
          <Descriptions
            size="small"
            column={2}
            bordered
            style={{ marginTop: 4, marginBottom: 12 }}
          >
            <Descriptions.Item label="Variant ID">
              <Typography.Text code>{variantId}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="BC SKU">
              {product?.bcSku && product.bcSku !== "NA" ? (
                <Typography.Text code>{product.bcSku}</Typography.Text>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="SAP SKU">
              {product?.sapSku ? (
                <Typography.Text code>{product.sapSku}</Typography.Text>
              ) : product?.raw?.prod_id ? (
                <Typography.Text code>{product.raw.prod_id}</Typography.Text>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              {product?.type && product.type !== "NA" ? (
                <Tag>{product.type}</Tag>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Active on store">
              {product?.active ? (
                <Tag color="green">Yes</Tag>
              ) : (
                <Tag color="default">No</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Subscription">
              {product?.subscription === "YES" ? (
                <Tag color="blue">Yes</Tag>
              ) : (
                <Tag color="default">No</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="BC inventory">
              {product?.bcInventory === "NA" ? (
                <Typography.Text type="secondary">NA</Typography.Text>
              ) : (
                <Typography.Text strong={Number(product?.bcInventory) < 5}>
                  {product?.bcInventory ?? 0}
                </Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Available / Reserved">
              <Typography.Text>
                {product?.availableStock ?? 0} / {product?.reservedStock ?? 0}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Price" span={2}>
              <Space size={12} wrap>
                <span>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    Sale
                  </Typography.Text>{" "}
                  <Typography.Text>{product?.salePriceDisplay || "—"}</Typography.Text>
                </span>
                <span>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    MSRP
                  </Typography.Text>{" "}
                  <Typography.Text>{product?.msrpDisplay || "—"}</Typography.Text>
                </span>
              </Space>
            </Descriptions.Item>
          </Descriptions>

          <Typography.Text
            type="secondary"
            style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}
          >
            Bucket context
          </Typography.Text>
          <Descriptions
            size="small"
            column={1}
            bordered
            style={{ marginTop: 4, marginBottom: 16 }}
          >
            <Descriptions.Item label="Store">
              <Space size={6}>
                <Tag color="geekblue" style={{ marginRight: 0 }}>
                  {storeLabel}
                </Tag>
                <Typography.Text code style={{ fontSize: 11 }}>
                  {storeHash}
                </Typography.Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Current">
              {assignment ? (
                <Space wrap>
                  {currentBucket ? (
                    <>
                      <Typography.Text>{currentBucket.displayName}</Typography.Text>
                    </>
                  ) : (
                    <Tag>bucket id {assignment.bucketId}</Tag>
                  )}
                  
                </Space>
              ) : (
                <Typography.Text type="secondary">Not yet assigned</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Is Default">
              {assignment ? (
                Number(assignment.isDefault) === 1 ? (
                  <Tag color="gold" icon={<StarFilled />}>
                    Default
                  </Tag>
                ) : (
                  <Tag>Not default</Tag>
                )
              ) : (
                <Typography.Text type="secondary">Not yet assigned</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Product type">
              {assignment?.productType ? (
                <Tag color="purple">
                  {assignment.productType.typeName || assignment.productType.type_name}
                </Tag>
              ) : (
                <Typography.Text type="secondary">None</Typography.Text>
              )}
            </Descriptions.Item>
          </Descriptions>

          {noBucketsAvailable ? (
            <Empty
              description={
                <span>
                  No active buckets exist for <b>{storeLabel}</b>. Create one in the{" "}
                  <b>Buckets</b> tab first.
                </span>
              }
            />
          ) : (
            <Form
              form={form}
              layout="vertical"
              disabled={isBusy}
              initialValues={{ is_default: 0 }}
              onValuesChange={checkDirty}
            >

              
              <Form.Item
                name="prod_type_id"
                label="Product type"
                rules={[{ required: true, message: "Select a product type" }]}
                extra="Tags this assignment with a product type for this store."
              >
                <Select
                  showSearch
                  placeholder="Select a product type"
                  options={productTypeOptions}
                  optionFilterProp="label"
                  loading={loadingBuckets}
                  notFoundContent={
                    loadingBuckets ? (
                      <Spin size="small" />
                    ) : productTypes.length === 0 ? (
                      "No product types for this store"
                    ) : (
                      "No matching product type"
                    )
                  }
                />
              </Form.Item>



              <Form.Item
                name="bucket_id"
                label={
                  <span>
                    Select bucket
                    {!isBucketRequired && (
                      <Typography.Text type="secondary" style={{ fontWeight: 400, marginLeft: 6 }}>
                        (optional)
                      </Typography.Text>
                    )}
                  </span>
                }
                rules={[{ required: isBucketRequired, message: "Pick a bucket" }]}
              >
                <Select
                  showSearch
                  disabled={isBucketDisabled}
                  placeholder="Choose a bucket for this variant"
                  options={bucketOptions}
                  filterOption={(input, opt) => {
                    const q = input.toLowerCase();
                    return (
                      (opt?.bucketKey || "").toLowerCase().includes(q) ||
                      (opt?.displayName || "").toLowerCase().includes(q)
                    );
                  }}
                  loading={loadingBuckets}
                  notFoundContent={
                    loadingBuckets ? <Spin size="small" /> : "No matching bucket"
                  }
                />
              </Form.Item>

              <Form.Item
                name="is_default"
                label={
                  <Space size={6}>
                    <StarFilled style={{ color: "#faad14" }} />
                    <span>Set as default product for this bucket</span>
                  </Space>
                }
                valuePropName="checked"
                getValueFromEvent={(v) => (v ? 1 : 0)}
                getValueProps={(v) => ({ checked: Number(v) === 1 })}
                extra="Used by the recommendation engine as the fallback when no scored match exists for this bucket's slot. Only one default per (bucket, store) — turning this on will demote any existing default."
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="custom_text"
                label="Custom text"
                extra="Optional — max 255 characters. Shown alongside this product in the recommendation output."
                rules={[
                  { max: 255, message: "Custom text must be 255 characters or fewer" },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === "") return Promise.resolve();
                      if (!/[a-zA-Z]/.test(value)) return Promise.reject("Must contain at least one letter. Only numbers not allowed.");
                      if (/[^a-zA-Z0-9\s_\-.,!?'"''""&()/:;]/.test(value)) return Promise.reject("Special characters like @#%^*+=<>{}[]\\|~` are not allowed.");
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <TextArea
                  rows={3}
                  maxLength={255}
                  showCount
                  placeholder="Enter optional custom text for this product assignment..."
                  allowClear
                />
              </Form.Item>

              {wouldReplaceDefault && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: -4, marginBottom: 12 }}
                  message="This will replace the current default"
                  description={
                    <span>
                      Variant <b>{wouldReplaceDefault.sapName} {wouldReplaceDefault.variantId}</b> is currently the
                      default for{" "}
                      <b>
                        {replacedBucket?.displayName ||
                          replacedBucket?.bucketKey ||
                          `bucket ${wouldReplaceDefault.bucketId}`}
                      </b>{" "}
                      in <b>{storeLabel}</b>. Saving will demote it (the server does this
                      atomically in the same transaction).
                    </span>
                  }
                />
              )}

              <Alert
                type="info"
                showIcon
                style={{ marginTop: 8 }}
                message="Saving uses the idempotent upsert endpoint — it will create a new assignment or update the existing one in a single call."
              />
            </Form>
          )}
        </Spin>
      )}
    </Drawer>
  );
};

export default ProductBucketDrawer;
